import { useEffect, useRef, useCallback } from "react";
import { useState } from "react";
import { toast } from "react-toastify";

class WebSocketService {
  constructor() {
    // Core WebSocket connection
    this.client = null;                    // The actual WebSocket instance
    this.isConnected = false;              // Boolean flag for connection state

    // Health monitoring timers
    this.pingInterval = null;              // Timer for sending periodic ping messages (5 minutes)
    this.healthCheckInterval = null;       // Timer for checking connection health (5 seconds)

    // Message handling
    this.chunkedPayloads = new Map();      // Stores partial chunked messages until complete

    // Event system - simple pub/sub for WebSocket events
    this.listeners = new Map();            // Map<eventName, Array<callback>>

    // Auto-reconnection properties
    this.reconnectTimer = null;             // Timer for reconnection attempts
    this.reconnectAttempts = 0;             // Current reconnection attempt count
    this.maxReconnectAttempts = 10;         // Maximum reconnection attempts (10 total attempts)
    this.fixedReconnectDelay = 4000;        // Fixed 8-second delay between attempts
    this.isReconnecting = false;            // Flag indicating reconnection in progress
    this.lastConnectionUrl = null;          // Last successful connection URL
    this.manualDisconnect = false;          // Flag for intentional disconnects
    this.hasReceivedSeedData = false;       // Flag indicating if seed data has been received
  }

  /**
   * Event System Methods
   * These provide a simple publish/subscribe pattern for WebSocket events
   * Similar to EventEmitter but without external dependencies
   */

  // Subscribe to an event
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Emit an event to all subscribers
  emit(event, data) {
    const callbacks = this.listeners.get(event) || [];
    callbacks.forEach((callback) => callback(data));
  }

  // Unsubscribe a specific callback from an event
  removeListener(event, callback) {
    const callbacks = this.listeners.get(event) || [];
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  // Remove all listeners (used during disconnect cleanup)
  removeAllListeners() {
    this.listeners.clear();
  }

  /**
   * Connection Management
   */

  // Establish WebSocket connection to the given URL
  async connect(url) {
    try {
      // Store the URL for potential reconnection
      this.lastConnectionUrl = url;
      this.manualDisconnect = false;

      // Clear any existing reconnection timer
      this.clearReconnectTimer();

      // Create new WebSocket instance
      this.client = new WebSocket(url);
    

      // Set up event handlers for the WebSocket
      this.setupEventHandlers();

      // Set up browser network event listeners (online/offline, visibility)
      this.setupNetworkListeners();

      // Return a Promise that resolves when connection opens or rejects on error
      return new Promise((resolve, reject) => {
        // Handle successful connection
        this.client.onopen = () => {
          this.isConnected = true;
          // Don't reset isReconnecting here - let the reconnection process handle it
          this.reconnectAttempts = 0; // Reset attempt counter on successful connection
          // Start health monitoring once connected
          this.startPingInterval();
          // Notify subscribers that we're connected
          this.emit("connect");
          resolve();
        };

        // Handle connection errors
        this.client.onerror = (error) => {
          this.isConnected = false;
          this.emit("error", error);
          reject(error);
        };
      });
    } catch (error) {
      // Handle synchronous errors (like invalid URL)
      toast.error("WebSocket connection failed");
      console.error("WebSocket connection failed:", error);
      throw error;
    }
  }

  /**
   * Network Event Listeners
   * These monitor browser-level network events that could affect WebSocket connection
   */

  // Clean up network event listeners (called during disconnect)
  removeNetworkListeners() {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  // Set up listeners for browser network events
  setupNetworkListeners() {
    // Store handler references so we can remove them later
    this.onlineHandler = () => {
      // Network came back online - attempt reconnection if we were connected before
      if (!this.isConnected && !this.isReconnecting && this.lastConnectionUrl) {
        this.scheduleReconnect("Network connection restored");
      }
    };

    // Handle when network goes offline
    this.offlineHandler = () => {
      if (this.isConnected) {
        this.isConnected = false;
        // Emit error when network is lost
        this.emit("error", new Error("Network connection lost"));
        // Schedule reconnection when network comes back
        this.scheduleReconnect("Network connection lost");
      }
    };

    // Handle when user switches browser tabs (visibility changes)
    this.visibilityHandler = () => {
      if (document.hidden) {
        // Tab became hidden - connection might become unstable
      } else {
        // Tab became visible again - check if connection is still good
        if (this.client && this.client.readyState !== WebSocket.OPEN && !this.isReconnecting) {
          this.isConnected = false;
          // Emit error if connection was lost while tab was hidden
          this.emit("error", new Error("Connection lost while tab was hidden"));
          this.scheduleReconnect("Connection lost while tab was hidden");
        }
      }
    };

    // Register the event listeners
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * WebSocket Event Handlers
   * These handle the core WebSocket events from the browser WebSocket API
   */

  // Set up handlers for WebSocket events
  setupEventHandlers() {
    if (!this.client) return;

    // Handle WebSocket errors (network issues, server errors, etc.)
    this.client.onerror = (error) => {
      this.isConnected = false;
      this.emit("error", error);
    };

    // Handle WebSocket connection close
    this.client.onclose = (event) => {

      this.isConnected = false;
      // Stop health monitoring when connection closes
      this.stopPingInterval();
      // Notify subscribers of close event with close code and reason
      this.emit("close", event);

      // Only attempt reconnection if this wasn't a manual disconnect
      if (!this.manualDisconnect) {
        // Don't reconnect for close codes that indicate permanent auth issues
        const permanentCloseCodes = [4001, 4003]; // Authentication errors

        // Handle different close scenarios
        if (permanentCloseCodes.includes(event.code)) {
          // Permanent auth errors - don't reconnect
          this.emit("reconnectFailed", {
            attempts: 0,
            reason: `Authentication failed (code ${event.code})`
          });
        } else {
          // All other close codes (including deployment draining) should trigger reconnection
          const isDeploymentDrain = [1001, 1012].includes(event.code); // Going away, Service restart
          const reconnectReason = isDeploymentDrain
            ? `Server deployment/maintenance (code ${event.code})`
            : `Connection closed: ${event.code} ${event.reason || ''}`;

          if (isDeploymentDrain) {
            // For deployment draining, use shorter delay since server is intentionally disconnecting
            this.scheduleReconnect(reconnectReason, 1000); // 1 second delay for deployments
          } else {
            this.scheduleReconnect(reconnectReason);
          }
        }
      }
    };

    // Handle incoming messages
    this.client.onmessage = (event) => {
      this.handleMessage(event);
    };
  }

  /**
   * Message Handling
   * Processes incoming WebSocket messages, including chunked messages for large data
   */

  // Process incoming WebSocket messages
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // Handle deployment notifications (server about to restart)
      if (data.action === "deployment" || data.type === "server_restart") {
        this.handleDeploymentNotification(data);
        return;
      }

      // Check if this is a chunked message (for large payloads)
      if (data.isChunked) {
        this.handleChunkedMessage(data);
        return;
      }

      // Emit regular messages to subscribers
      this.emit("message", data);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
      this.emit("error", error);
    }
  }

  // Handle deployment notifications from server
  handleDeploymentNotification(data) {

    // Emit event for UI to show deployment warning
    this.emit("deployment", {
      message: data.message || "Server is restarting for deployment",
      estimatedDowntime: data.estimatedDowntime || 30, // seconds
      canReconnect: data.canReconnect !== false, // Default to true
    });

    // If server indicates we should reconnect after a delay
    if (data.reconnectAfter && typeof data.reconnectAfter === 'number') {
      setTimeout(() => {
        if (!this.manualDisconnect && this.lastConnectionUrl) {
          this.scheduleReconnect("Server deployment completed", data.reconnectAfter);
        }
      }, data.reconnectAfter);
    }
  }

  // Handle chunked messages (for large payloads that are split into pieces)
  handleChunkedMessage(data) {
    const { messageId, chunkIndex, totalChunks, chunkData, action } = data;

    // Initialize storage for this message if it's the first chunk
    if (!this.chunkedPayloads.has(messageId)) {
      this.chunkedPayloads.set(messageId, {
        chunks: [],        // Array to store chunks in order
        received: 0,       // Number of chunks received so far
        total: totalChunks, // Total number of chunks expected
        action,            // The original message action
      });
    }

    // Store this chunk and increment received count
    const payload = this.chunkedPayloads.get(messageId);
    payload.chunks[chunkIndex] = chunkData;
    payload.received += 1;

    // Check if we have all chunks
    if (payload.received === totalChunks) {
      // Reassemble the full message
      const fullPayloadStr = payload.chunks.join("");
      try {
        const fullPayload = JSON.parse(fullPayloadStr);
        // Emit the complete message
        this.emit("message", fullPayload);
        // Clean up the stored chunks
        this.chunkedPayloads.delete(messageId);
      } catch (error) {
        console.error("Failed to parse chunked payload:", error);
        this.emit("error", error);
        // Clean up even on error
        this.chunkedPayloads.delete(messageId);
      }
    }
  }

  /**
   * Message Sending
   */

  // Send a message through the WebSocket connection
  send(message) {
    // Check if WebSocket is available and in OPEN state
    if (!this.client || this.client.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket is not connected. Message not sent:", message);
      return false;
    }

    try {
      // Convert message to string if it's an object
      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);
      this.client.send(messageStr);
      return true;
    } catch (error) {
      console.error("Failed to send WebSocket message:", error);
      return false;
    }
  }

  /**
   * Connection Health Monitoring
   * Uses ping messages and periodic health checks to detect connection issues
   */

  // Start the connection health monitoring system
  startPingInterval() {
    // Clear any existing ping timer
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    // Send ping messages every 5 minutes to keep connection alive and detect issues
    this.pingInterval = setInterval(() => {
      if (this.isConnected && this.client && this.client.readyState === WebSocket.OPEN) {
        const success = this.send("ping");
        if (!success) {
          this.isConnected = false;
          this.emit("error", new Error("Connection lost"));
          this.scheduleReconnect("Ping failed");
        }
      }
    }, 300000); // 5 minutes

    // Set up more frequent health checks (every 5 seconds)
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      // Check if WebSocket is still in OPEN state
      if (this.client && this.client.readyState !== WebSocket.OPEN && !this.isReconnecting) {
        this.isConnected = false;
        this.emit("error", new Error("Connection lost"));
        this.scheduleReconnect("Health check detected connection loss");
      }
    }, 5000); // Check every 5 seconds

  }

  /**
   * Auto-Reconnection Logic
   * Handles automatic reconnection with fixed delay timing
   */

  // Clear any pending reconnection timer
  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Mark that seed data has been received (enables reconnection)
  markSeedDataReceived() {
    this.hasReceivedSeedData = true;
  
  }

  // Reset seed data flag (used on logout)
  resetSeedDataFlag() {
    this.hasReceivedSeedData = false;
  }

  // Schedule reconnection with fixed delay timing
  scheduleReconnect(reason, immediateDelay = null) {
    if (this.manualDisconnect || !this.lastConnectionUrl) {
      return;
    }

    // Only attempt reconnection if we have received seed data
    if (!this.hasReceivedSeedData) {
      return;
    }

    // Prevent multiple concurrent reconnection attempts
    if (this.isReconnecting && !immediateDelay) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      
      this.isReconnecting = false; // Reconnection process failed
      this.emit("reconnectFailed", { attempts: this.reconnectAttempts, reason });
      return;
    }

    this.clearReconnectTimer();

    // Use immediate delay if provided (for deployment scenarios), otherwise use fixed delay
    const delay = immediateDelay !== null ? immediateDelay : this.fixedReconnectDelay;

    this.reconnectTimer = setTimeout(() => {
      this.performReconnect();
    }, delay);
  }

  // Perform the actual reconnection attempt
  async performReconnect() {
    if (this.manualDisconnect || !this.lastConnectionUrl || this.isReconnecting) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    this.emit("reconnecting", { attempt: this.reconnectAttempts, maxAttempts: this.maxReconnectAttempts });

    try {

      const user = JSON.parse(localStorage.getItem("user"));
      const url = new URL(this.lastConnectionUrl);
      url.searchParams.set('token', user.accessToken);
      this.lastConnectionUrl = url.toString();

      // Disconnect current connection if it exists
      if (this.client) {
        this.client.close();
        this.client = null;
      }

      await this.connect(this.lastConnectionUrl);
      this.isReconnecting = false; // Reconnection process complete
    } catch (error) {
      // Only log errors when online (Sentry can report them)
      if (navigator.onLine) {
        console.error(`[RECONNECT] Attempt ${this.reconnectAttempts} failed , Internet is online`);
      }
      this.isReconnecting = false;

      // Any reconnection failure could be due to expired tokens, so emit auth error
      // to trigger token refresh and retry
      this.emit("authError", {
        attempt: this.reconnectAttempts,
        error: error.message,
        suggestion: "Connection failed, token may have expired - app should refresh tokens"
      });

      // Schedule next reconnection attempt
      this.scheduleReconnect(`Reconnection attempt failed: ${error.message}`);
    }
  }

  // Manual reconnection attempt (triggered by user)
  async manualReconnect(user = null) {
    if (this.manualDisconnect || !this.lastConnectionUrl) {
      return false;
    }

    // Reset reconnection state for manual attempt
    this.clearReconnectTimer();
    this.isReconnecting = true;
    this.reconnectAttempts = 0; // Reset attempts for manual reconnect

    try {
      // If user is provided, check if tokens need refresh proactively
      if (user) {
      
        const url = new URL(this.lastConnectionUrl);
        url.searchParams.set('token', user.accessToken);
        this.lastConnectionUrl = url.toString();
        console.log("[MANUAL RECONNECT] Updated connection URL with current token");
      }

      // Disconnect current connection if it exists
      if (this.client) {
        this.client.close();
        this.client = null;
      }

      await this.connect(this.lastConnectionUrl);
      this.isReconnecting = false; // Manual reconnection process complete
      return true;
    } catch (error) {
      console.error("[MANUAL RECONNECT] Manual reconnection failed:", error.message);
      this.isReconnecting = false;

      // For manual reconnection, don't emit authError to avoid triggering automatic reconnection
      // Just return false and let the UI handle the failure (show error message, etc.)
      return false;
    }
  }

  // Stop all health monitoring timers
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.clearReconnectTimer();
  }

  /**
   * Disconnection & Cleanup
   */

  // Gracefully disconnect from WebSocket and clean up all resources
  disconnect() {
    this.manualDisconnect = true;     // Mark as intentional disconnect
    this.isConnected = false;
    this.isReconnecting = false;      // Cancel any reconnection attempts
    // Stop health monitoring and reconnection timers
    this.stopPingInterval();
    // Remove network event listeners
    this.removeNetworkListeners();

    // Close WebSocket connection if it exists
    if (this.client) {
      this.client.close();
      this.client = null;
    }

    // Clean up any pending chunked messages
    this.chunkedPayloads.clear();
    // Remove all event listeners
    this.removeAllListeners();
  }

  /**
   * Status & Utilities
   */

  // Get current connection status information
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,           // Our internal connection flag
      readyState: this.client?.readyState,     // WebSocket readyState (0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED)
      networkOnline: navigator.onLine,          // Browser's network online status
      isReconnecting: this.isReconnecting,     // Whether we're currently attempting reconnection
      reconnectAttempts: this.reconnectAttempts, // Number of reconnection attempts made
      maxReconnectAttempts: this.maxReconnectAttempts, // Maximum allowed reconnection attempts
    };
  }
}

/**
 * Singleton WebSocket service instance
 * All React components using useWebSocket will share the same connection
 */
const webSocketService = new WebSocketService();

/**
 * React Hook for WebSocket communication
 *
 * This hook provides a React-friendly interface to the WebSocketService.
 * It manages event listener cleanup and provides convenient methods for
 * subscribing to messages and managing the connection.
 */
export const useWebSocket = () => {
  const listenersRef = useRef(new Map());

  // Cleanup function - removes all listeners when component unmounts
  const cleanup = useCallback(() => {
    listenersRef.current.forEach((listener, event) => {
      webSocketService.removeListener(event, listener);
    });
    listenersRef.current.clear();
  }, []);

  // Subscribe to WebSocket events (connect, error, close, message)
  const subscribe = useCallback((event, callback) => {
    // Remove existing listener for this event if any (prevents duplicates)
    if (listenersRef.current.has(event)) {
      webSocketService.removeListener(event, listenersRef.current.get(event));
    }

    // Add new listener and track it for cleanup
    webSocketService.on(event, callback);
    listenersRef.current.set(event, callback);
  }, []);

  // Subscribe to specific message types by action (e.g., "login", "update")
  const subscribeToMessage = useCallback((action, callback) => {
    // Create a filtered listener that only calls callback for matching actions
    const listener = (data) => {
      if (data.action === action) {
        callback(data);
      }
    };

    // Remove existing listener for this action if any
    const key = `message:${action}`;
    if (listenersRef.current.has(key)) {
      webSocketService.removeListener("message", listenersRef.current.get(key));
    }

    // Add new listener and track it for cleanup
    webSocketService.on("message", listener);
    listenersRef.current.set(key, listener);
  }, []);

  // Send a message through the WebSocket connection
  const send = useCallback((message) => {
    return webSocketService.send(message);
  }, []);

  // Get current connection status
  const getStatus = useCallback(() => {
    return webSocketService.getConnectionStatus();
  }, []);

  // Connect to WebSocket server
  const connect = useCallback(async (url) => {
    return webSocketService.connect(url);
  }, []);

  // Disconnect from WebSocket server
  const disconnect = useCallback(() => {
    webSocketService.disconnect();
  }, []);

  // Manual reconnection (triggered by user)
  const manualReconnect = useCallback(async (user = null) => {
    return webSocketService.manualReconnect(user);
  }, []);

  // Enhanced event handlers with user feedback
  const handleReconnecting = useCallback((data) => {
    // Reconnection attempts are now shown in the notification component
  }, []);

  const handleReconnectFailed = useCallback((data) => {
    if (navigator.onLine) {
      console.error("Reconnection failed after", data.attempts, "attempts:", data.reason);
    }
   
  }, []);

  // Automatic cleanup when component unmounts
  useEffect(() => {
    // Setup automatic event handlers for user feedback
    webSocketService.on("reconnecting", handleReconnecting);
    webSocketService.on("reconnectFailed", handleReconnectFailed);

    return () => {
      // Remove automatic handlers
      webSocketService.removeListener("reconnecting", handleReconnecting);
      webSocketService.removeListener("reconnectFailed", handleReconnectFailed);

      // Run user cleanup
      cleanup();
    };
  }, [cleanup, handleReconnecting, handleReconnectFailed]);

  // Return hook interface
  return {
    // Core methods
    send,           // Send messages
    connect,        // Establish connection
    disconnect,     // Close connection
    manualReconnect, // Manual reconnection
    subscribe,      // Subscribe to events
    subscribeToMessage, // Subscribe to specific message actions
    getStatus,      // Get connection status

    // Status properties
    isConnected: webSocketService.isConnected, // Current connection state
    isReconnecting: webSocketService.isReconnecting, // Whether reconnection is in progress
    reconnectAttempts: webSocketService.reconnectAttempts, // Current attempt count
    maxReconnectAttempts: webSocketService.maxReconnectAttempts, // Max attempts allowed

    // Convenience methods (shorthand aliases)
    onMessage: subscribeToMessage,                    // Alias for subscribeToMessage
    onConnect: (callback) => subscribe("connect", callback),      // Connection established
    onDisconnect: (callback) => subscribe("close", callback),     // Connection closed
    onError: (callback) => subscribe("error", callback),          // Connection errors
    onReconnecting: (callback) => subscribe("reconnecting", callback), // Reconnection attempts
    onReconnectFailed: (callback) => subscribe("reconnectFailed", callback), // Reconnection failed
    onDeployment: (callback) => subscribe("deployment", callback), // Server deployment notifications
    onAuthError: (callback) => subscribe("authError", callback),   // Authentication errors during reconnection

    // Control methods
    markSeedDataReceived: () => webSocketService.markSeedDataReceived(), // Enable reconnection after seed data
    resetSeedDataFlag: () => webSocketService.resetSeedDataFlag(), // Reset seed data flag on logout
  };
};

/**
 * Utility Hooks
 */

/**
 * Hook for subscribing to specific message actions
 * Automatically subscribes when action/callback change
 */
export const useWebSocketMessage = (action, callback) => {
  const { subscribeToMessage } = useWebSocket();

  useEffect(() => {
    if (action && callback) {
      subscribeToMessage(action, callback);
    }
  }, [action, callback, subscribeToMessage]);
};

/**
 * Hook for reactive connection status
 * Returns an object that updates when connection state changes
 */
export const useWebSocketStatus = () => {
  const [status, setStatus] = useState(webSocketService.getConnectionStatus());

  useEffect(() => {
    // Update status whenever connection events occur
    const updateStatus = () =>
      setStatus(webSocketService.getConnectionStatus());

    webSocketService.on("connect", updateStatus);
    webSocketService.on("close", updateStatus);
    webSocketService.on("error", updateStatus);
    webSocketService.on("reconnecting", updateStatus);

    return () => {
      webSocketService.removeListener("connect", updateStatus);
      webSocketService.removeListener("close", updateStatus);
      webSocketService.removeListener("error", updateStatus);
      webSocketService.removeListener("reconnecting", updateStatus);
    };
  }, []);
 
  return status;
};

/**
 * Direct Service Access & Utilities
 * For use outside of React components
 */

// Export the service instance for direct access
export { webSocketService };

// Send message with error handling (non-React contexts)
export const sendWebSocketMessage = (message) => {
  const success = webSocketService.send(message);
  if (!success) {
    console.warn("WebSocket message not sent - connection not available");
    // Emit event for potential UI feedback (reconnection prompts, etc.)
    webSocketService.emit("sendFailed", { message, timestamp: Date.now() });
  }
  return success;
};

// Utility functions for checking connection state
export const isWebSocketConnected = () => webSocketService.isConnected;
export const getWebSocketStatus = () => webSocketService.getConnectionStatus();
